import LogManager from './classes/log-manager'
import { Log, ErrorLog, EventLog, SetiLog, WarningLog, LOG_TYPES } from './classes/logs'
import ElasticSearchProvider from './providers/elastic-search-provider'
import SplunkProvider from './providers/splunk-provider'
import GTMProvider from './providers/gtm-provider'
import GA4Provider from './providers/ga4-provider'

import BrowserErrorBehavior from './behaviors/browser-error-behavior'
import PageVisibilityBehavior from './behaviors/page-visibility-behavior'
import { updatePCLNData } from './utils/pcln-data'
import Storage from './utils/storage'
import { uniqueString } from './utils/string'

const _browserErrorBehavior = uniqueString('PCLNAnalytics_browserErrorBehavior')
const _showDebugs = uniqueString('PCLNAnalytics_showDebugs')
const _loggedKeys = uniqueString('PCLNAnalytics_loggedKeys')
const _pageVisibilityBehavior = uniqueString('PCLNAnalytics_ pageVisibilityBehavior')
const _elasticSearchProvider = uniqueString('PCLNAnalytics_elasticSearchProvider')
const _gtmProvider = uniqueString('PCLNAnalytics_gtmProvider')
const _splunkProvider = uniqueString('PCLNAnalytics_splunkProvider')
const _ga4Provider = uniqueString('PCLNAnalytics_ga4provider')

export default class PCLNAnalytics {
  constructor(config) {
    config = { ...config, ...Storage.state }

    this[_showDebugs] = false
    this[_loggedKeys] = {}
    this[_elasticSearchProvider] = new ElasticSearchProvider(config)
    this[_splunkProvider] = new SplunkProvider(config)
    this[_gtmProvider] = new GTMProvider({
      max: config.maxLogs,
      ...config,
    })
    this[_ga4Provider] = new GA4Provider(config)

    this[_browserErrorBehavior] = new BrowserErrorBehavior((error) => this.logError(error))
    this[_pageVisibilityBehavior] = new PageVisibilityBehavior((hidden) => {
      if (hidden) LogManager.instance.release({ manual: true })
    })

    this.name = config.name
    this.version = config.version
    this.debugMode = config.debug || false
    this.GAEnabled = typeof config.GAEnabled === 'boolean' ? config.GAEnabled : true
    this.GA4Enabled = typeof config.GA4Enabled === 'boolean' ? config.GA4Enabled : false
  }

  set appName(appName) {
    updatePCLNData({ appName })
  }

  set appVersion(appVersion) {
    updatePCLNData({ appVersion })
  }

  set debugMode(state = false) {
    this[_showDebugs] = state

    Storage.save('debug', state)
  }

  set enableGA4(state = true) {
    this.GA4Enabled = state
  }

  log(data, priority) {
    const valid = this.validateLog(data, LOG_TYPES.DEFAULT)

    if (valid) this[_elasticSearchProvider].log(data, priority)
  }

  logError(data = {}, priority) {
    const valid = this.validateLog(data, LOG_TYPES.ERROR)

    if (valid) this[_elasticSearchProvider].log(new ErrorLog(data, this[_showDebugs]), priority)
  }

  getEvent(data) {
    return data instanceof Log ? data : new EventLog(data)
  }

  logElasticSearch(event, priority, flattenedData) {
    const flatEvent = flattenedData ? this.getEvent(flattenedData) : event
    this[_elasticSearchProvider].log(flatEvent, priority)
  }

  logEvent(data = {}, priority, flattenedData) {
    if (data.event === 'seti') {
      this.logSeti(data, priority)

      return
    }

    const valid = this.validateLog(data, LOG_TYPES.EVENT)
    const event = this.getEvent(data)

    if (valid) {
      if (this.GAEnabled) this[_gtmProvider].log(event)
      if (this.GA4Enabled) this[_ga4Provider].log(event)
      this.logElasticSearch(event, priority, flattenedData)
    }
  }

  logSeti(data = {}) {
    this.logEvent(new SetiLog(data))
  }

  // often only want to log a SETI dimension once per app instance
  logSetiOnce(data = {}, logKey) {
    if (logKey && this[_loggedKeys][logKey]) {
      return
    }

    this[_loggedKeys][logKey] = true
    this.logEvent(new SetiLog(data))
  }

  logWarning(data = {}, priority) {
    const valid = this.validateLog(data, LOG_TYPES.WARNING)

    if (valid) this[_elasticSearchProvider].log(new WarningLog(data, this[_showDebugs]), priority)
  }

  validateLog(log, type = LOG_TYPES.DEFAULT) {
    const valid = Log.isLog(log)

    if (valid) {
      if (!(log instanceof Log) && log.type) {
        const message = `PCLNAnalytics.validateLog -> log.type should not be specified -> Overriding ${log.type} with ${type}.`

        this.logWarning({ message })
      }
    } else {
      const severity = 9
      const message = `PCLNAnalytics.validateLog -> Invalid Log -> type: ${typeof log}, value: ${log}`

      this.logError({ severity, message })
    }

    return valid
  }
}
